import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import api from "../../utils/api";
import html2pdf from "html2pdf.js";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import ViolationLetterTemplate from "./ViolationLetterTemplate";
import useCompanyLogo from "../../utils/useCompanyLogo";
import { useClientConfig } from "../../utils/ClientConfigContext";

const DEFAULT_TEMPLATE_CONTENT = `
<p><strong>Property Address: {{property_address}}</strong></p>
<p>Dear Homeowner:</p>
<p>
  This letter is being sent as a courtesy notice. As of the date of this letter,
  certain conditions may exist that violate the Governing Documents of your
  community. The specific rule and corrective action are referenced below.
</p>
<p><strong>Observation:</strong></p>
<p><strong>{{ViolationType}}</strong></p>
<p><strong>{{violation_article}}</strong> - <em>{{violation_rule}}</em></p>
<p><strong>Required Action:</strong> {{required_action}}</p>
<p>
  Thank you in advance for your attention to this matter. If you cannot correct
  the condition immediately, please notify me in writing within {{days_to_cure}}
  days of your plans to correct the condition.
</p>
<p>
  Your community is controlled by governing documents that identify the
  conditions and restrictions established to maintain property values for every
  homeowner. Feel free to contact me if you do not have a copy of these
  documents. Again, I appreciate your cooperation in resolving this matter. If
  you have any questions, I can be reached at the number or email address found
  below.
</p>
<p>Sincerely,</p>
<p><strong>Thomas Lambert</strong></p>
<p>Compliance Officer</p>
<p>compliance@fairfieldplantation.com</p>
<p>770-832-2360</p>
`;

const dialogPaperSx = {
  borderRadius: "20px",
  p: 1,
  width: "100%",
  maxWidth: 600,
  boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    minHeight: 56,
  },
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: "12px",
  fontWeight: 600,
  px: 3,
  py: 1.25,
};

const ViolationLetterTemplatesPage = ({ member, violationDetails }) => {
  const logoUrl = useCompanyLogo();
  const { violationLetterTemplate, updateViolationLetterTemplate } = useClientConfig();
  const [tplSaveSuccess, setTplSaveSuccess] = useState(false);
  const [templates, setTemplates] = useState({});
  const [templateMeta, setTemplateMeta] = useState({});
  const [selectedLetter, setSelectedLetter] = useState("");
  const [viewMode, setViewMode] = useState("preview");
  const [loading, setLoading] = useState(true);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newTemplateType, setNewTemplateType] = useState("");
  const [newTemplateName, setNewTemplateName] = useState("");

  const letterRef = useRef();
  const today = new Date();

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/violation-letter-templates`);
        const templatesFromDB = res.data || [];

        const tmpl = {};
        const meta = {};

        templatesFromDB.forEach((tpl) => {
          tmpl[tpl.type] = tpl.content;
          meta[tpl.type] = {
            id: tpl.id,
            name: tpl.name,
          };
        });

        setTemplates(tmpl);
        setTemplateMeta(meta);

        if (templatesFromDB.length > 0) {
          setSelectedLetter(templatesFromDB[0].type);
        }
      } catch (err) {
        console.error("Error loading violation templates:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const getDeliveryMethod = (tpl) => {
    if (!tpl) return "Delivered via US Mail and Email";
    switch (tpl.id) {
      case 1:
        return "Delivered via US Mail and Email";
      case 4:
        return "Delivered via Certified Mail and Email";
      case 5:
        return "Delivered via Email";
      default:
        return "Delivered via US Mail and Email";
    }
  };

  const handleOpenAddTemplateDialog = () => {
    setNewTemplateType("");
    setNewTemplateName("");
    setOpenAddDialog(true);
  };

  const handleCloseAddTemplateDialog = () => {
    setOpenAddDialog(false);
  };

  const handleCreateTemplate = () => {
    const normalizedType = newTemplateType.trim();
    const normalizedName = newTemplateName.trim() || normalizedType;

    if (!normalizedType) {
      alert("Template type is required.");
      return;
    }

    if (templates[normalizedType]) {
      alert("A template with that type already exists.");
      setSelectedLetter(normalizedType);
      setOpenAddDialog(false);
      return;
    }

    setTemplates((prev) => ({
      ...prev,
      [normalizedType]: DEFAULT_TEMPLATE_CONTENT,
    }));

    setTemplateMeta((prev) => ({
      ...prev,
      [normalizedType]: {
        id: null,
        name: normalizedName,
      },
    }));

    setSelectedLetter(normalizedType);
    setViewMode("rich");
    setOpenAddDialog(false);
  };

  const handleDownload = () => {
    if (!letterRef.current) return;

    html2pdf()
      .set({
        margin: 0.5,
        filename: `${selectedLetter || "Violation"}_Letter.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      })
      .from(letterRef.current)
      .save();
  };

  const handleSave = async () => {
    if (!selectedLetter) {
      alert("Select a letter type first.");
      return;
    }

    const { id, name } = templateMeta[selectedLetter] || {
      id: null,
      name: `${selectedLetter} Template`,
    };

    const content = templates[selectedLetter];

    try {
      if (id) {
        await api.put(`/violation-letter-templates/${id}`, {
          name,
          type: selectedLetter,
          content,
          is_default: 0,
        });
        alert("Template updated");
      } else {
        const res = await api.post(`/violation-letter-templates`, {
          name,
          type: selectedLetter,
          content,
          is_default: 0,
          created_by: null,
        });

        setTemplateMeta((prev) => ({
          ...prev,
          [selectedLetter]: {
            ...prev[selectedLetter],
            id: res.data.id,
            name,
          },
        }));

        alert("Template created");
      }
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save template");
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 5 }} />;

  const availableTypes = Object.keys(templates);

  const TEMPLATE_OPTIONS = [
    {
      value: "1",
      label: "Standard",
      description: "Centered header with logo above the letter body.",
      preview: (
        <Box sx={{ width: "100%", p: 1.5, fontFamily: "sans-serif" }}>
          <Box sx={{ textAlign: "center", borderBottom: "1.5px solid #ccc", pb: 1, mb: 1 }}>
            <Box sx={{ width: 36, height: 20, bgcolor: "#d0dff5", borderRadius: 1, mx: "auto", mb: 0.5 }} />
            <Box sx={{ width: 80, height: 6, bgcolor: "#223142", borderRadius: 1, mx: "auto", mb: 0.5 }} />
            <Box sx={{ width: 56, height: 4, bgcolor: "#aab", borderRadius: 1, mx: "auto" }} />
          </Box>
          {[80, 60, 90, 50].map((w, i) => (
            <Box key={i} sx={{ width: `${w}%`, height: 4, bgcolor: "#e0e6ef", borderRadius: 1, mb: 0.75 }} />
          ))}
        </Box>
      ),
    },
    {
      value: "2",
      label: "Window Envelope",
      description: "Address block on left, logo and title on right. prints through a window envelope.",
      preview: (
        <Box sx={{ width: "100%", p: 1.5, fontFamily: "sans-serif" }}>
          <Box sx={{ display: "flex", gap: 1, borderBottom: "1.5px solid #ccc", pb: 1, mb: 1, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ width: 48, height: 4, bgcolor: "#aab", borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ width: 64, height: 4, bgcolor: "#aab", borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ width: 52, height: 4, bgcolor: "#aab", borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ width: 32, height: 18, bgcolor: "#d0dff5", borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ width: 60, height: 5, bgcolor: "#223142", borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ width: 44, height: 4, bgcolor: "#aab", borderRadius: 1 }} />
            </Box>
          </Box>
          {[80, 60, 90, 50].map((w, i) => (
            <Box key={i} sx={{ width: `${w}%`, height: 4, bgcolor: "#e0e6ef", borderRadius: 1, mb: 0.75 }} />
          ))}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Violation Letter Templates
      </Typography>

      {/* ── Letter Layout Picker ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.10)",
          backgroundColor: "#f8fafd",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Letter Layout
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Choose the layout used when generating and sending violation letters.
          This applies system-wide.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {TEMPLATE_OPTIONS.map((opt) => {
            const selected = violationLetterTemplate === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={async () => {
                  await updateViolationLetterTemplate(opt.value);
                  setTplSaveSuccess(true);
                }}
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 280,
                  cursor: "pointer",
                  borderRadius: "14px",
                  border: selected
                    ? "2px solid #2f63ad"
                    : "2px solid rgba(47,99,173,0.12)",
                  backgroundColor: selected ? "rgba(47,99,173,0.05)" : "#fff",
                  overflow: "hidden",
                  transition: "border-color 0.18s, box-shadow 0.18s",
                  boxShadow: selected
                    ? "0 4px 16px rgba(47,99,173,0.14)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
                  "&:hover": {
                    borderColor: "#2f63ad",
                    boxShadow: "0 4px 16px rgba(47,99,173,0.14)",
                  },
                }}
              >
                {/* Mini preview */}
                <Box
                  sx={{
                    bgcolor: "#f0f4fa",
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {opt.preview}
                </Box>

                {/* Label row */}
                <Box sx={{ p: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#223142" }}>
                      {opt.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: "#6b778c", mt: 0.25 }}>
                      {opt.description}
                    </Typography>
                  </Box>
                  {selected && (
                    <CheckCircleIcon sx={{ color: "#2f63ad", fontSize: 20, flexShrink: 0, mt: 0.25 }} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Snackbar
        open={tplSaveSuccess}
        autoHideDuration={2500}
        onClose={() => setTplSaveSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setTplSaveSuccess(false)}>
          Letter layout saved.
        </Alert>
      </Snackbar>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mt: 2, flexWrap: "wrap" }}
      >
        {availableTypes.length > 0 ? (
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Letter Type</InputLabel>
            <Select
              value={selectedLetter}
              label="Letter Type"
              onChange={(e) => setSelectedLetter(e.target.value)}
            >
              {availableTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {templateMeta[type]?.name || type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No violation templates found in the database.
          </Typography>
        )}

        <Button
          variant="outlined"
          onClick={handleOpenAddTemplateDialog}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            borderRadius: "6px",
          }}
        >
          Add New Template
        </Button>
      </Stack>

      {selectedLetter && (
        <>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Template Name"
              value={templateMeta[selectedLetter]?.name || ""}
              onChange={(e) =>
                setTemplateMeta((prev) => ({
                  ...prev,
                  [selectedLetter]: {
                    ...prev[selectedLetter],
                    name: e.target.value,
                  },
                }))
              }
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
            >
              <ToggleButton value="preview">Preview</ToggleButton>
              <ToggleButton value="edit">Edit HTML</ToggleButton>
              <ToggleButton value="rich">Rich Text</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === "edit" ? (
            <TextField
              fullWidth
              multiline
              minRows={20}
              variant="outlined"
              value={templates[selectedLetter] || ""}
              onChange={(e) =>
                setTemplates((prev) => ({
                  ...prev,
                  [selectedLetter]: e.target.value,
                }))
              }
              sx={{ mt: 3 }}
            />
          ) : viewMode === "rich" ? (
            <Box sx={{ mt: 3 }}>
              <ReactQuill
                theme="snow"
                value={templates[selectedLetter] || ""}
                onChange={(value) =>
                  setTemplates((prev) => ({
                    ...prev,
                    [selectedLetter]: value,
                  }))
                }
                style={{ height: "400px", marginBottom: "40px" }}
              />
            </Box>
          ) : (
            <Paper
              elevation={3}
              sx={{
                mt: 3,
                p: 3,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            >
              <div ref={letterRef}>
                <ViolationLetterTemplate
                  type={templateMeta[selectedLetter]?.name}
                  member={member}
                  violation={violationDetails}
                  date={today}
                  content={templates[selectedLetter]}
                  deliveryMethod={getDeliveryMethod(templateMeta[selectedLetter])}
                  logoUrl={logoUrl}
                />
              </div>
            </Paper>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={handleDownload}>
              Download PDF
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Template
            </Button>
          </Stack>
        </>
      )}

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddTemplateDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.5rem",
            fontWeight: 500,
            color: "#2b2b2b",
            px: 3,
            pt: 3,
            pb: 1,
          }}
        >
          Add New Template
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Template Type"
              value={newTemplateType}
              onChange={(e) => setNewTemplateType(e.target.value)}
              fullWidth
              placeholder="final_notice"
              sx={inputSx}
            />

            <TextField
              label="Template Name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              fullWidth
              placeholder="Final Notice"
              sx={inputSx}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleCloseAddTemplateDialog}
            sx={{
              ...actionButtonSx,
              color: "#2f6fdb",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "rgba(47, 111, 219, 0.06)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateTemplate}
            sx={{
              ...actionButtonSx,
              backgroundColor: "#3f6fc0",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#355fa7",
                boxShadow: "none",
              },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViolationLetterTemplatesPage;